import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  api,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  clearSessionTokens,
  getStoredAccessToken,
  storeSessionTokens,
} from "@/api/client";
import { toast } from "@/components/ui/sonner";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem("project_local_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [lastOrder, setLastOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getStoredAccessToken());
  const sessionVersion = useRef(0);

  const refresh = useCallback(async () => {
    const version = sessionVersion.current;
    setLoading(true);
    try {
      const bootstrap = token ? await apiGet("/tenant/bootstrap") : await apiGet("/demo/bootstrap");
      if (version !== sessionVersion.current) return;
      setData(bootstrap);
      setUser(bootstrap.user || null);
    } catch (error) {
      if (version !== sessionVersion.current) return;
      const status = error?.response?.status;
      const code = error?.response?.data?.detail?.error?.code;
      // Keep a valid session when tenant bootstrap fails (e.g. DB provision retry).
      // Only clear tokens for real auth failures.
      const authGone = status === 401 || code === "AUTH_REQUIRED" || code === "INVALID_TOKEN" || code === "USER_NOT_FOUND";
      if (token && !authGone) {
        try {
          const me = await apiGet("/auth/me");
          if (version !== sessionVersion.current) return;
          setUser(me.user || me);
          setData((prev) => prev || {
            user: me.user || me,
            business: me.business || null,
            categories: [],
            products: [],
            orders: [],
            customers: [],
            business_types: [],
            themes: [],
          });
        } catch {
          // still keep token; UI may show auth-required until retry
        }
        return;
      }
      clearSessionTokens();
      setToken(null);
      const bootstrap = await apiGet("/demo/bootstrap");
      if (version !== sessionVersion.current) return;
      setData(bootstrap);
      setUser(null);
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    window.localStorage.setItem("project_local_cart", JSON.stringify(cart));
  }, [cart]);

  const loadTenantBootstrap = async (fallbackUser, fallbackBusiness = null) => {
    try {
      const bootstrap = await apiGet("/tenant/bootstrap");
      setData(bootstrap);
      setUser(bootstrap.user || fallbackUser);
      return bootstrap;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) throw error;
      // Session tokens are already stored — allow onboarding/dashboard to load and retry.
      setData({
        user: fallbackUser,
        business: fallbackBusiness,
        categories: [],
        products: [],
        orders: [],
        customers: [],
        business_types: [],
        themes: [],
      });
      setUser(fallbackUser);
      toast.message("Signed in — finishing store setup. Refresh if data looks incomplete.");
      return null;
    }
  };

  const login = async (email, password) => {
    const result = await apiPost("/auth/login", { email, password });
    if (result.mfa_required) {
      return result;
    }
    sessionVersion.current += 1;
    storeSessionTokens(result);
    setToken(result.token);
    setUser(result.user);
    if (result.mfa_setup_required) {
      toast.message("Enable MFA in Settings — required for platform admins");
    }
    if (result.user?.role !== "platform_admin") {
      await loadTenantBootstrap(result.user, result.business);
    } else {
      setData({
        user: result.user,
        business: null,
        categories: [],
        products: [],
        orders: [],
        customers: [],
        business_types: [],
        themes: [],
        admin: true,
      });
    }
    toast.success("Signed in to BuildHof");
    return result;
  };

  const completeMfaLogin = async (mfaToken, code) => {
    const result = await apiPost("/auth/mfa/verify", { mfa_token: mfaToken, code });
    sessionVersion.current += 1;
    storeSessionTokens(result);
    setToken(result.token);
    setUser(result.user);
    if (result.user?.role !== "platform_admin") {
      await loadTenantBootstrap(result.user, result.business);
    }
    toast.success("Signed in to BuildHof");
    return result;
  };

  const signup = async (name, email, password) => {
    const result = await apiPost("/auth/signup", { name, email, password });
    sessionVersion.current += 1;
    storeSessionTokens(result);
    setToken(result.token);
    setUser(result.user);
    await loadTenantBootstrap(result.user, result.business);
    toast.success("Account created — your business tenant is ready");
    return result;
  };

  const logout = async () => {
    sessionVersion.current += 1;
    const refreshToken = window.localStorage.getItem("project_local_refresh_token");
    clearSessionTokens();
    delete api.defaults.headers.common.Authorization;
    setToken(null);
    setUser(null);
    setData(null);
    setLoading(false);
    try {
      await apiPost("/auth/logout", refreshToken ? { refresh_token: refreshToken } : {});
    } catch {
      // Local session is already cleared; server cookie cleanup is best-effort.
    }
    toast.success("Logged out");
  };

  const changePassword = async (currentPassword, newPassword) => {
    await apiPost("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
    toast.success("Password changed");
  };

  const addToCart = (product, options = {}) => {
    setCart((current) => {
      const existing = current.find((item) => item.product_id === product.id);
      if (existing) {
        return current.map((item) => item.product_id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...current, { product_id: product.id, name: product.name, price: product.price, quantity: 1 }];
    });
    toast.success(`${product.name} added to cart`);
    if (options.slug) {
      import("@/lib/analytics").then(({ trackAnalyticsEvent }) => {
        trackAnalyticsEvent({
          event_type: "add_to_cart",
          slug: options.slug,
          product_id: product.id,
        });
      }).catch(() => {});
    }
  };

  const clearCart = () => {
    setCart([]);
    window.localStorage.removeItem("project_local_cart");
  };

  const updateCartQuantity = (productId, quantity) => {
    setCart((current) => current.map((item) => item.product_id === productId ? { ...item, quantity: Math.max(1, quantity) } : item));
  };

  const removeFromCart = (productId) => {
    setCart((current) => current.filter((item) => item.product_id !== productId));
  };

  const updateOrderStatus = async (orderId, status, estimatedReadyTime) => {
    const updated = await apiPatch(`/orders/${orderId}/status`, { status, estimated_ready_time: estimatedReadyTime });
    setData((current) => ({
      ...current,
      orders: current.orders.map((order) => order.id === orderId ? updated : order),
    }));
    toast.success(`Order ${updated.order_number} moved to ${status.replaceAll("_", " ")}`);
    return updated;
  };

  const patchOrder = async (orderId, payload) => {
    const updated = await apiPatch(`/orders/${orderId}`, payload);
    setData((current) => ({
      ...current,
      orders: current.orders.map((order) => order.id === orderId ? updated : order),
    }));
    toast.success("Order updated");
    return updated;
  };

  const updateStoreOperations = async (payload) => {
    const result = await apiPatch("/merchant-hq/store-operations", payload);
    setData((current) => ({
      ...current,
      business: { ...current.business, store_operations: result.operations },
    }));
    toast.success("Store status updated");
    return result.operations;
  };

  const patchBusiness = async (payload) => {
    const updated = await apiPatch("/business", payload);
    setData((current) => ({ ...current, business: updated }));
    toast.success("Store settings updated");
    return updated;
  };

  const patchProduct = async (productId, payload) => {
    const updated = await apiPatch(`/products/${productId}`, payload);
    setData((current) => ({
      ...current,
      products: current.products.map((product) => product.id === productId ? updated : product),
    }));
    toast.success("Product updated");
    return updated;
  };

  const createProduct = async (payload) => {
    const created = await apiPost("/products", payload);
    setData((current) => ({ ...current, products: [...current.products, created] }));
    toast.success("Product added");
    return created;
  };

  const deleteProduct = async (productId) => {
    await apiDelete(`/products/${productId}`);
    setData((current) => ({ ...current, products: current.products.filter((product) => product.id !== productId) }));
    toast.success("Product deleted");
  };

  const createCategory = async (name) => {
    const created = await apiPost("/categories", { name });
    setData((current) => ({ ...current, categories: [...current.categories, created] }));
    toast.success("Category added");
    return created;
  };

  const patchCategory = async (categoryId, payload) => {
    const updated = await apiPatch(`/categories/${categoryId}`, payload);
    setData((current) => ({
      ...current,
      categories: current.categories.map((category) => category.id === categoryId ? updated : category).sort((a, b) => a.sort_order - b.sort_order),
    }));
    toast.success("Category updated");
    return updated;
  };

  const deleteCategory = async (categoryId) => {
    await apiDelete(`/categories/${categoryId}`);
    setData((current) => ({ ...current, categories: current.categories.filter((category) => category.id !== categoryId) }));
    toast.success("Category deleted");
  };

  const duplicateProduct = async (productId) => {
    const created = await apiPost(`/products/${productId}/duplicate`, {});
    setData((current) => ({ ...current, products: [...current.products, created] }));
    toast.success("Product duplicated");
  };

  const importExampleProducts = async () => {
    const result = await apiPost("/products/import-examples", {});
    if (result.items?.length) {
      setData((current) => ({ ...current, products: [...current.products, ...result.items] }));
      toast.success("Example products imported");
    } else {
      toast.info(result.message || "No examples imported");
    }
    return result;
  };

  const userRole = user?.role || "owner";
  const isOwner = userRole === "owner";
  const isManager = userRole === "manager";
  const isStaff = userRole === "staff";

  const value = { data, loading, cart, lastOrder, setLastOrder, user, token, userRole, isOwner, isManager, isStaff, isAuthenticated: Boolean(token && user), login, completeMfaLogin, signup, logout, changePassword, addToCart, updateCartQuantity, removeFromCart, clearCart, refresh, updateOrderStatus, patchOrder, updateStoreOperations, patchBusiness, patchProduct, createProduct, deleteProduct, duplicateProduct, importExampleProducts, createCategory, patchCategory, deleteCategory };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useProjectLocal() {
  const context = useContext(AppContext);
  if (!context) throw new Error("useProjectLocal must be used inside AppProvider");
  return context;
}